import { useEffect, useState } from "react";
import { api } from "../lib/api";
import { useAuth } from "../context/AuthContext";
import { Link, useNavigate } from "react-router-dom";
import { Avatar } from "../components/Layout";
import Tour from "../components/Tour";
import { fmtDate } from "../lib/dates";
import { STEP_STATUS } from "../lib/bouquet";
import { Sprout, Users, CalendarDays, Lock, Sparkles, Flower2, ArrowRight } from "lucide-react";
import { toast } from "sonner";

export default function BouquetHome() {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [data, setData] = useState(null);
  const [moments, setMoments] = useState([]);
  const [tourDone, setTourDone] = useState(false);

  const load = () => {
    api.get("/bouquet").then((r) => setData(r.data)).catch(() => {});
    api.get("/moments").then((r) => setMoments((r.data || []).slice(0, 3))).catch(() => {});
  };
  useEffect(() => { load(); }, []);

  if (!data) return <div className="animate-pulse text-muted-foreground text-sm">Gathering your bouquet…</div>;

  const postpone = async (stepId) => {
    await api.post(`/steps/${stepId}/postpone`);
    toast.success("No rush — we'll check back in a few days");
    load();
  };

  const dismiss = async (stepId) => {
    await api.post(`/steps/${stepId}/dismiss`);
    load();
  };

  return (
    <div data-testid="bouquet-home" className="space-y-10">
      {!tourDone && !user.tour_seen && <Tour onDone={() => setTourDone(true)} />}
      <div className="space-y-4 pt-2" data-testid="bouquet-greeting">
        <h2 className="font-display text-3xl sm:text-5xl font-medium tracking-tight leading-tight">
          What's growing, {user.name.split(" ")[0]}?
        </h2>
        <p className="text-muted-foreground text-sm sm:text-base">Room for your next step—and your people.</p>
        <div>
          <Link to="/growing" state={{ create: true }} data-testid="name-whats-growing"
            className="inline-flex items-center gap-2 bg-primary text-primary-foreground px-6 py-3.5 rounded-full text-sm font-semibold hover:opacity-90 transition-opacity">
            Name what's growing <ArrowRight className="h-4 w-4" />
          </Link>
        </div>
      </div>

      {data.prompts.length > 0 && (
        <div className="bg-rose-pale border border-border rounded-xl p-5 sm:p-6 space-y-4" data-testid="reflection-prompts">
          {data.prompts.map((p) => (
            <div key={p.step_id} className="flex flex-wrap items-center gap-3" data-testid={`prompt-${p.step_id}`}>
              <Sprout className="h-5 w-5 text-sage shrink-0" />
              <div className="flex-1 min-w-[200px]">
                <p className="text-sm font-semibold">Want to reflect on this?</p>
                <p className="text-sm text-muted-foreground">"{p.text}" — {fmtDate(p.target_date)}</p>
              </div>
              <div className="flex gap-2">
                <button data-testid={`prompt-reflect-${p.step_id}`}
                  onClick={() => navigate("/growing", { state: { reflect: p.step_id } })}
                  className="px-4 py-2 rounded-full bg-primary text-primary-foreground text-xs font-semibold">How did it go?</button>
                <button data-testid={`prompt-later-${p.step_id}`} onClick={() => postpone(p.step_id)}
                  className="px-4 py-2 rounded-full border border-border text-xs font-semibold hover:bg-secondary transition-colors">Later</button>
                <button data-testid={`prompt-dismiss-${p.step_id}`} onClick={() => dismiss(p.step_id)}
                  className="px-3 py-2 rounded-full text-xs text-muted-foreground hover:text-foreground transition-colors">Not now</button>
              </div>
            </div>
          ))}
        </div>
      )}

      <section className="space-y-4" data-testid="whats-growing-section">
        <h3 className="font-display text-2xl font-medium flex items-center gap-2">
          <Sprout className="h-5 w-5 text-sage" /> What's Growing
        </h3>
        {data.intentions.length === 0 && (
          <div className="bg-card border border-border rounded-xl p-8 space-y-2">
            <p className="font-display text-lg font-medium">Nothing planted yet</p>
            <p className="text-sm text-muted-foreground max-w-md">Whatever matters to you right now counts. It stays private until you choose to share it.</p>
          </div>
        )}
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          {data.intentions.map((i) => (
            <Link key={i.intention_id} to="/growing" data-testid={`growing-card-${i.intention_id}`}
              className="bg-card border border-border rounded-xl p-5 block hover:border-primary transition-colors">
              <p className="font-display text-lg font-medium leading-snug">{i.text}</p>
              {i.current_step ? (
                <div className="mt-4 pt-4 border-t border-border space-y-2">
                  <p className="text-[10px] font-semibold tracking-[0.2em] uppercase text-muted-foreground">Next step</p>
                  <p className="text-sm">{i.current_step.text}</p>
                  <div className="flex flex-wrap items-center gap-x-2 gap-y-1 text-[11px] pt-1 text-muted-foreground">
                    {i.current_step.shared_pod_name ? (
                      <span className="flex items-center gap-1 text-primary font-semibold"><Users className="h-3 w-3" /> Supported by {i.current_step.shared_pod_name}</span>
                    ) : (
                      <span className="flex items-center gap-1"><Lock className="h-3 w-3" /> Only you</span>
                    )}
                    <span>·</span>
                    {i.current_step.target_date ? <span>by {fmtDate(i.current_step.target_date)}</span> : <span>Optional date</span>}
                  </div>
                  <p className="text-xs font-semibold text-primary pt-1 flex items-center gap-1">
                    View next step <ArrowRight className="h-3 w-3" />
                  </p>
                </div>
              ) : (
                <p className="text-xs text-muted-foreground mt-3">No next step yet</p>
              )}
            </Link>
          ))}
        </div>
      </section>

      <section className="space-y-4" data-testid="my-pods-section">
        <h3 className="font-display text-2xl font-medium flex items-center gap-2">
          <Users className="h-5 w-5 text-sage" /> My Pods
        </h3>
        {data.pods.length === 0 ? (
          <div className="bg-sage-pale border border-border rounded-xl p-8 sm:p-10 text-center space-y-4" data-testid="empty-pods-card">
            <Flower2 className="h-8 w-8 text-primary mx-auto" />
            <p className="font-display text-xl sm:text-2xl font-medium">A little circle of people you trust.</p>
            <p className="text-sm text-muted-foreground max-w-md mx-auto">Make room to connect, share a next step, and show up for each other.</p>
            <Link to="/pods" state={{ create: true }} data-testid="start-a-pod"
              className="inline-flex items-center gap-2 bg-primary text-primary-foreground px-5 py-2.5 rounded-full text-sm font-semibold hover:opacity-90 transition-opacity">
              Start a pod
            </Link>
          </div>
        ) : (
          <div className="space-y-3">
            {data.pods.map((p) => (
              <Link key={p.pod_id} to={`/pods/${p.pod_id}`} data-testid={`pod-chip-${p.pod_id}`}
                className="bg-card border border-border rounded-xl p-4 flex items-center gap-4 hover:border-primary transition-colors">
                <div className="flex -space-x-2">
                  {(p.members || []).slice(0, 4).map((m) => <Avatar key={m.user_id} user={m} size="h-8 w-8 ring-2 ring-card" />)}
                </div>
                <div className="min-w-0">
                  <p className="text-sm font-semibold truncate">{p.name}</p>
                  <p className="text-xs text-muted-foreground truncate">{p.description || `${p.members?.length || 0} people`}</p>
                </div>
              </Link>
            ))}
          </div>
        )}
      </section>

      {data.gatherings.length > 0 && (
        <section className="space-y-4" data-testid="upcoming-gatherings-preview">
          <h3 className="font-display text-2xl font-medium flex items-center gap-2">
            <CalendarDays className="h-5 w-5 text-sage" /> Coming Up
          </h3>
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-3">
            {data.gatherings.map((g) => (
              <Link key={g.event_id} to={g.pod_id ? `/pods/${g.pod_id}` : "/community"} data-testid={`gathering-preview-${g.event_id}`}
                className="bg-card border border-border rounded-xl overflow-hidden hover:border-primary transition-colors">
                {g.image_path && (
                  <img src={`${api.defaults.baseURL}/files/${g.image_path}`} alt={g.title} className="w-full h-24 object-cover" data-testid={`gathering-preview-image-${g.event_id}`} />
                )}
                <div className="p-4 space-y-1">
                  <div className="flex items-start justify-between gap-2">
                    <p className="text-sm font-semibold truncate">{g.title}</p>
                    <span className="text-xs font-semibold text-primary shrink-0">{fmtDate(g.date)}{g.time ? ` · ${g.time}` : ""}</span>
                  </div>
                  <p className="text-xs text-muted-foreground truncate">{g.pod_name}{g.location ? ` · ${g.location}` : ""}</p>
                  {g.my_rsvp && (
                    <span className="inline-block px-2 py-0.5 rounded-full bg-sage-pale text-[10px] font-semibold" data-testid={`gathering-preview-rsvp-${g.event_id}`}>
                      {g.my_rsvp === "going" ? "Going" : g.my_rsvp === "maybe" ? "Maybe" : "Can't make it"}
                    </span>
                  )}
                </div>
              </Link>
            ))}
          </div>
        </section>
      )}

      {moments.length > 0 && (
        <section className="space-y-4" data-testid="recent-moments-section">
          <div className="flex items-center justify-between">
            <h3 className="font-display text-2xl font-medium flex items-center gap-2">
              <Sparkles className="h-5 w-5 text-sage" /> Recent Moments
            </h3>
            <Link to="/moments" data-testid="goto-moments" className="text-xs font-semibold text-primary hover:underline">All moments →</Link>
          </div>
          <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
            {moments.map((m) => (
              <Link key={m.moment_id} to="/moments" data-testid={`home-moment-${m.moment_id}`}
                className="bg-card border border-border rounded-xl p-4 block hover:border-primary transition-colors">
                <p className="text-sm font-semibold truncate">{m.title}</p>
                <p className="text-xs text-muted-foreground mt-0.5">{m.author?.name} · {fmtDate(m.date)}</p>
              </Link>
            ))}
          </div>
        </section>
      )}

      <section className="space-y-3" data-testid="my-communities-section">
        <h3 className="font-display text-2xl font-medium">My Communities</h3>
        {data.communities.length === 0 ? (
          <p className="text-sm text-muted-foreground">Communities you identify with will appear here.</p>
        ) : (
          <div className="flex flex-wrap gap-2">
            {data.communities.map((c) => (
              <Link key={c.chapter_id} to="/community" data-testid={`community-${c.chapter_id}`}
                className="px-4 py-2 rounded-full bg-secondary text-sm font-medium hover:bg-primary hover:text-primary-foreground transition-colors">{c.name}</Link>
            ))}
          </div>
        )}
      </section>
    </div>
  );
}
